import React, { useEffect } from "react";
import "@/App.css";
import { Clock, Star, Shield, Phone, Mail, MapPin, Users, Plane, Heart, Truck, GraduationCap, Briefcase } from "lucide-react";

const App = () => {
  useEffect(() => {
    document.title = "baltini-transport.com";
  }, []);
  const services = [
    {
      title: "Staff Transport",
      description: "Reliable daily transportation for your team. Professional service every day.",
      icon: Briefcase,
      color: "var(--blue)"
    },
    {
      title: "School Transport",
      description: "Safe and secure transportation for students. Trusted by families.",
      icon: GraduationCap,
      color: "var(--red)"
    },
    {
      title: "Tours & Excursions",
      description: "Comfortable group travel for events, tours, weddings, and more.",
      icon: MapPin,
      color: "var(--blue)"
    },
    {
      title: "Airport Transfers",
      description: "Reliable pickups and drop-offs at Hato International. Stress-free travel.",
      icon: Plane,
      color: "var(--red)"
    },
    {
      title: "Septic Tank Services",
      description: "Professional septic tank pumping and cleaning. Solving your problems since 2008.",
      icon: Truck,
      color: "var(--blue)"
    }
  ];

  const testimonials = [
    {
      text: "Baltini Transport made our wedding day absolutely perfect! The bus was spotless and all our guests arrived on time. Highly recommend!",
      name: "Maria van der Berg",
      location: "Netherlands"
    },
    {
      text: "We booked a full island tour for our family reunion. The driver knew all the best spots and even took us to hidden beaches!",
      name: "James & Sarah Thompson",
      location: "Miami, USA"
    },
    {
      text: "Used Baltini for our corporate retreat. Punctual, comfortable, and the coordination was flawless. Will use again!",
      name: "Roberto Silva",
      location: "São Paulo, Brazil"
    }
  ];

  return (
    <div className="app-container" data-testid="app-container">
      {/* Header */}
      <header className="header" data-testid="header">
        <div className="header-content">
          <a href="#" className="logo" data-testid="logo">
            <img 
              src="https://customer-assets.emergentagent.com/job_journey-hub-169/artifacts/iscgnik6_IMG_2404.jpg" 
              alt="Baltini Transport"
              className="logo-image"
            />
          </a>
          <nav className="nav" data-testid="navigation">
            <a href="#services" className="nav-link">Services</a>
            <a href="#about" className="nav-link">About</a>
            <a href="#reviews" className="nav-link">Reviews</a>
            <a href="tel:+59995103347" className="nav-btn" data-testid="header-call-btn">
              <Phone size={18} />
              <span>Call Us</span>
            </a>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero" data-testid="hero-section">
        <div className="hero-content">
          <div className="hero-text">
            <span className="hero-badge">Welcome to Baltini Transport</span>
            <h1 className="hero-title">
              Your Trusted<br/>
              <span>Transport Partner</span><br/>
              in Curaçao
            </h1>
            <p className="hero-desc">
              We bring families, friends, and colleagues together with safe, comfortable, and reliable bus charter services across the beautiful island of Curaçao.
            </p>
            <div className="hero-buttons">
              <a href="tel:+59995103347" className="btn-primary" data-testid="call-now-btn">
                <Phone size={20} />
                Call +599 9 510 3347
              </a>
              <a href="#services" className="btn-secondary" data-testid="services-btn">
                Our Services
              </a>
            </div>
          </div>
          <div className="hero-image">
            <div className="hero-image-wrapper">
              <img 
                src="https://customer-assets.emergentagent.com/job_journey-hub-169/artifacts/lmxxswc5_IMG_9106.PNG" 
                alt="Baltini Transport Bus with Happy Customers"
              />
            </div>
            <div className="hero-badge-card">
              <Shield size={24} />
              <div>
                <strong>Safe & Reliable</strong>
                <span>Licensed & Insured</span>
              </div>
            </div>
          </div>
        </div>
        <div className="hero-wave">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="var(--sand-light)"/>
          </svg>
        </div>
      </section>

      {/* Features Strip */}
      <section className="features" data-testid="features-section">
        <div className="features-container">
          <div className="feature-item" data-testid="feature-0">
            <div className="feature-icon"><Clock size={24} /></div>
            <span>Always On Time</span>
          </div>
          <div className="feature-item" data-testid="feature-1">
            <div className="feature-icon"><Star size={24} /></div>
            <span>5-Star Rated</span>
          </div>
          <div className="feature-item" data-testid="feature-2">
            <div className="feature-icon"><Shield size={24} /></div>
            <span>Fully Insured</span>
          </div>
          <div className="feature-item" data-testid="feature-3">
            <div className="feature-icon"><Users size={24} /></div>
            <span>Professional Drivers</span>
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="services" data-testid="services-section">
        <div className="services-container">
          <div className="section-header">
            <span className="section-tag">What We Offer</span>
            <h2 className="section-title">Our Services</h2>
            <p className="section-desc">From airport transfers to island adventures, we've got you covered.</p>
          </div>
          <div className="services-grid">
            {services.map((service, index) => (
              <div key={index} className="service-card" data-testid={`service-${index}`}>
                <div className="service-icon" style={{backgroundColor: service.color}}>
                  <service.icon size={28} />
                </div>
                <h3>{service.title}</h3>
                <p>{service.description}</p>
                <a href="tel:+59995103347" className="service-link">
                  Inquire Now →
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About / Bus Image Section */}
      <section id="about" className="about" data-testid="about-section">
        <div className="about-container">
          <div className="about-image">
            <img 
              src="https://customer-assets.emergentagent.com/job_journey-hub-169/artifacts/lmxxswc5_IMG_9106.PNG" 
              alt="Baltini Transport Bus"
            />
          </div>
          <div className="about-content">
            <span className="section-tag">About Us</span>
            <h2>Your Journey, Our Priority</h2>
            <p>
              At Baltini Transport, we believe every journey should be comfortable, safe, and enjoyable. Based in beautiful Curaçao, we've been serving locals and tourists with premium bus charter services.
            </p>
            <ul className="about-list">
              <li><Shield size={20} /> Licensed and fully insured</li>
              <li><Star size={20} /> Experienced, friendly drivers</li>
              <li><Clock size={20} /> Punctual and reliable service</li>
              <li><Users size={20} /> Comfortable, air-conditioned buses</li>
            </ul>
            <a href="tel:+59995103347" className="btn-primary">
              <Phone size={18} /> Get In Touch
            </a>
          </div>
        </div>
      </section>

      {/* Transport Solutions Banner */}
      <section className="banner-section" data-testid="banner-section">
        <div className="banner-container">
          <img 
            src="https://customer-assets-rejwkqb3.emergentagent.net/job_journey-hub-169/artifacts/7t6fz4t7_image.png"
            alt="Baltini Transport - Transport Solutions"
            className="banner-image"
          />
        </div>
      </section>

      {/* Septic Tank Services Section */}
      <section className="septic-section" data-testid="septic-section">
        <div className="septic-container">
          <div className="septic-content">
            <span className="section-tag">Since 2008</span>
            <h2>Septic Tank Services</h2>
            <p className="septic-tagline">Solving Your Problems</p>
            <p>
              Professional septic tank pumping, cleaning, and maintenance services for residential and commercial properties across Curaçao. Our experienced team and specialized equipment ensure efficient and hygienic service every time.
            </p>
            <ul className="about-list">
              <li><Truck size={20} /> Professional pumping & cleaning</li>
              <li><Shield size={20} /> Licensed and certified</li>
              <li><Clock size={20} /> Fast response times</li>
              <li><Star size={20} /> Trusted since 2008</li>
            </ul>
            <a href="tel:+59995103347" className="btn-primary">
              <Phone size={18} /> Call 510 3347
            </a>
          </div>
          <div className="septic-image">
            <img 
              src="https://customer-assets.emergentagent.com/job_journey-hub-169/artifacts/ufyikldp_IMG_9116.jpg" 
              alt="Baltini Transport Septic Tank Truck"
            />
          </div>
        </div>
      </section>

      {/* Septic Tank Solutions Banner */}
      <section className="banner-section" data-testid="septic-banner-section">
        <div className="banner-container">
          <img 
            src="https://customer-assets-rejwkqb3.emergentagent.net/job_journey-hub-169/artifacts/2bqmbfme_image.png"
            alt="Baltini Transport - Septic Tank Solutions"
            className="banner-image"
          />
        </div>
      </section>

      {/* Testimonials */}
      <section id="reviews" className="testimonials" data-testid="testimonials-section">
        <div className="testimonials-container">
          <div className="section-header center">
            <span className="section-tag">Happy Customers</span>
            <h2 className="section-title">What People Say</h2>
          </div>
          <div className="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="testimonial-card" data-testid={`testimonial-${index}`}>
                <div className="stars">★★★★★</div>
                <p>"{testimonial.text}"</p>
                <div className="testimonial-author">
                  <div className="author-avatar">
                    {testimonial.name.charAt(0)}
                  </div>
                  <div>
                    <strong>{testimonial.name}</strong>
                    <span>{testimonial.location}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta" data-testid="cta-section">
        <div className="cta-container">
          <h2>Ready to Start Your Journey?</h2>
          <p>Contact us today and let's plan your perfect transport experience in Curaçao.</p>
          <div className="cta-buttons">
            <a href="tel:+59995103347" className="btn-cta" data-testid="cta-call-btn">
              <Phone size={20} />
              Call +599 9 510 3347
            </a>
            <a href="mailto:baltini-transport@hotmail.com" className="btn-cta-outline" data-testid="cta-email-btn">
              <Mail size={20} />
              Email Us
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer" data-testid="footer">
        <div className="footer-container">
          <div className="footer-brand">
            <img 
              src="https://customer-assets.emergentagent.com/job_journey-hub-169/artifacts/iscgnik6_IMG_2404.jpg" 
              alt="Baltini Transport"
              className="footer-logo"
            />
            <p>Premium bus charter services in Curaçao</p>
          </div>
          <div className="footer-contact">
            <h4>Contact Us</h4>
            <div className="footer-contact-item">
              <Phone size={16} />
              <span>+599 9 510 3347</span>
            </div>
            <div className="footer-contact-item">
              <Mail size={16} />
              <span>baltini-transport@hotmail.com</span>
            </div>
            <div className="footer-contact-item">
              <MapPin size={16} />
              <span>Willemstad, Curaçao</span>
            </div>
            <a href="https://www.baltinitransport.com" target="_blank" rel="noopener noreferrer" className="footer-website">
              www.baltinitransport.com
            </a>
          </div>
          <div className="footer-links">
            <h4>Quick Links</h4>
            <a href="#services">Services</a>
            <a href="#about">About Us</a>
            <a href="#reviews">Reviews</a>
          </div>
        </div>
        <div className="footer-bottom">
          <p>© 2026 Baltini Transport. All rights reserved.</p>
          <a 
            href="https://app.emergent.sh/?utm_source=emergent-badge" 
            target="_blank" 
            rel="noopener noreferrer"
            className="footer-badge"
          >
            <img src="https://avatars.githubusercontent.com/in/1201222?s=120&u=2686cf91179bbafbc7a71bfbc43004cf9ae1acea&v=4" alt="Emergent" />
            Made with Emergent
          </a>
        </div>
      </footer>
    </div>
  );
};

export default App;
